import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  setDoc,
  deleteField,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase/firebase";
import { useAuth } from "../Context/AuthContext";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./AdminDashboard.scss";
import AdminBulkGuestImporter from "../AdminBulkGuestImporter/AdminBulkGuestImporter";
import AdminGuestFamilySummary from "../AdminGuestFamilySummary/AdminGuestFamilySummary";
import AdminInvoiceGenerator from "../AdminInvoiceGenerator/AdminInvoiceGenerator";

const TRIAL_DAYS = 7;
const ROWS_PER_PAGE = 10;
const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";
const MAX_BATCH_WRITES = 400;

const DEFAULT_ADDONS = {
  seatingArrangement: false,
  budgetCalculation: false,
  qrCode: false,
  digitalInvite: false,
  musicChange: false,
};

const TEST_FAMILY_NAME = "Test Family";

const AdminDashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addonFilter, setAddonFilter] = useState("all");
  const [registeredFrom, setRegisteredFrom] = useState("");
  const [registeredTo, setRegisteredTo] = useState("");
  const [weddingFrom, setWeddingFrom] = useState("");
  const [weddingTo, setWeddingTo] = useState("");
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  const [busyId, setBusyId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSiteSlug, setSelectedSiteSlug] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [addonsForm, setAddonsForm] = useState(DEFAULT_ADDONS);
  const [isSavingAddons, setIsSavingAddons] = useState(false);
  const [editableExpiryDate, setEditableExpiryDate] = useState("");
  const [isSavingExpiry, setIsSavingExpiry] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);

  const functions = getFunctions();
  const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");

  const toDateObject = (value) => {
    if (!value) return null;

    try {
      if (value?.toDate) {
        return value.toDate();
      }

      if (typeof value === "string" || value instanceof Date) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const normalizeDateOnly = (value) => {
    const date = toDateObject(value);
    if (!date) return null;

    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const toInputDateValue = (value) => {
    const date = normalizeDateOnly(value);
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  const toDateString = (value) => {
    const date = normalizeDateOnly(value);
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  const formatDate = (value) => {
    const date = toDateObject(value);
    if (!date) return "—";
    return date.toLocaleDateString();
  };

  const addDays = (value, daysToAdd) => {
    const baseDate = normalizeDateOnly(value);
    if (!baseDate) return null;

    const result = new Date(baseDate);
    result.setDate(result.getDate() + daysToAdd);
    return result;
  };

  const normalizeCeremonyType = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();

    if (normalized === "homecoming" || normalized === "home coming") {
      return "home coming";
    }

    return "wedding";
  };

  const getDefaultCeremonyTime = (ceremonyType) => {
    return normalizeCeremonyType(ceremonyType) === "home coming"
      ? "18:00"
      : "09:00";
  };

  const getNormalizedPlan = (user) => {
    if (user?.plan === "premium") return "premium";
    if (user?.plan === "trial") return "trial";
    if (user?.isTrial) return "trial";
    return "trial";
  };

  const isPremiumUser = (user) => getNormalizedPlan(user) === "premium";

  const getPackageLabel = (user) => {
    return isPremiumUser(user) ? "Premium" : "Trial";
  };

  const getMergedAddons = (user) => ({
    ...DEFAULT_ADDONS,
    ...(user?.addons || {}),
  });

  const getEnabledAddonCount = (user) =>
    Object.values(getMergedAddons(user)).filter(Boolean).length;

  const getMobileNumber = (user) =>
    user.mobile_number ||
    user.contact_number ||
    user.mobile ||
    user.phone ||
    user.phoneNumber ||
    user.mobileNumber ||
    "—";

  const getAuthUid = (user) =>
    user?.authUid || user?.uid || user?.firebaseUid || "";

  const getAutoExpiryDate = (user) => {
    if (isPremiumUser(user)) {
      return normalizeDateOnly(user.wedding_date);
    }

    return addDays(user.createdAt, TRIAL_DAYS);
  };

  const getEffectiveExpiryDate = (user) => {
    return normalizeDateOnly(user?.customExpiryDate) || getAutoExpiryDate(user);
  };

  const getExpiryLabel = (user) => {
    return formatDate(getEffectiveExpiryDate(user));
  };

  const isExpiredUser = (user) => {
    const expiryDate = getEffectiveExpiryDate(user);
    if (!expiryDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today >= expiryDate;
  };

  const isDateInRange = (target, from, to) => {
    const targetDate = normalizeDateOnly(target);
    if (!targetDate) return false;

    const fromDate = from ? normalizeDateOnly(from) : null;
    const toDate = to ? normalizeDateOnly(to) : null;

    if (fromDate && targetDate < fromDate) return false;
    if (toDate && targetDate > toDate) return false;

    return true;
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setPageError("");

      const usersQuery = query(
        collection(db, "users"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(usersQuery);

      const rows = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setUsers(rows);
    } catch (error) {
      console.error("Failed to load users:", error);
      setPageError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    planFilter,
    statusFilter,
    addonFilter,
    registeredFrom,
    registeredTo,
    weddingFrom,
    weddingTo,
    expiryFrom,
    expiryTo,
  ]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const currentPlan = getNormalizedPlan(user);
      const isExpired = isExpiredUser(user);
      const addonCount = getEnabledAddonCount(user);

      const matchesSearch =
        !term ||
        [
          user.bride_name,
          user.groom_name,
          user.email,
          user.slug,
          user.plan,
          user.user_type,
          getMobileNumber(user),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesPlan =
        planFilter === "all" ||
        (planFilter === "premium" && currentPlan === "premium") ||
        (planFilter === "trial" && currentPlan === "trial");

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "expired" && isExpired) ||
        (statusFilter === "active" && !isExpired);

      const matchesAddon =
        addonFilter === "all" ||
        (addonFilter === "withAddons" && addonCount > 0) ||
        (addonFilter === "withoutAddons" && addonCount === 0);

      const matchesRegistered =
        (!registeredFrom && !registeredTo) ||
        isDateInRange(user.createdAt, registeredFrom, registeredTo);

      const matchesWedding =
        (!weddingFrom && !weddingTo) ||
        isDateInRange(user.wedding_date, weddingFrom, weddingTo);

      const matchesExpiry =
        (!expiryFrom && !expiryTo) ||
        isDateInRange(getEffectiveExpiryDate(user), expiryFrom, expiryTo);

      return (
        matchesSearch &&
        matchesPlan &&
        matchesStatus &&
        matchesAddon &&
        matchesRegistered &&
        matchesWedding &&
        matchesExpiry
      );
    });
  }, [
    users,
    searchTerm,
    planFilter,
    statusFilter,
    addonFilter,
    registeredFrom,
    registeredTo,
    weddingFrom,
    weddingTo,
    expiryFrom,
    expiryTo,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ROWS_PER_PAGE));

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredUsers.slice(startIndex, startIndex + ROWS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  const siteUrl = selectedSiteSlug
    ? `https://www.planmyguest.com/sites/${selectedSiteSlug}`
    : "";

  const handleRowClick = (user) => {
    setSelectedSiteSlug(user.slug || "");
    setSelectedUser(user);
    setAddonsForm(getMergedAddons(user));
    setEditableExpiryDate(toInputDateValue(getEffectiveExpiryDate(user)));
  };

  const closeDetailsModal = () => {
    if (isSavingAddons || isSavingExpiry) return;
    setSelectedUser(null);
    setAddonsForm(DEFAULT_ADDONS);
    setEditableExpiryDate("");
  };

  const openAddonsFromButton = (user) => {
    setSelectedSiteSlug(user.slug || "");
    setSelectedUser(user);
    setAddonsForm(getMergedAddons(user));
    setEditableExpiryDate(toInputDateValue(getEffectiveExpiryDate(user)));
  };

  const handleAddonChange = (key) => {
    setAddonsForm((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveAddons = async () => {
    if (!selectedUser) return;

    try {
      setIsSavingAddons(true);

      const payload = {
        addons: addonsForm,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "users", selectedUser.id), payload);

      if (selectedUser.slug) {
        await updateDoc(doc(db, "sites", selectedUser.slug), payload);
      }

      await loadUsers();

      setSelectedUser((prev) =>
        prev
          ? {
            ...prev,
            addons: addonsForm,
          }
          : prev
      );
    } catch (error) {
      console.error("Failed to save add-ons:", error);
      alert("Failed to save add-ons.");
    } finally {
      setIsSavingAddons(false);
    }
  };

  const handleSaveExpiry = async () => {
    if (!selectedUser || !editableExpiryDate) {
      alert("Please select an expiry date.");
      return;
    }

    try {
      setIsSavingExpiry(true);

      const payload = {
        customExpiryDate: editableExpiryDate,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "users", selectedUser.id), payload);

      if (selectedUser.slug) {
        await updateDoc(doc(db, "sites", selectedUser.slug), payload);
      }

      await loadUsers();

      setSelectedUser((prev) =>
        prev
          ? {
            ...prev,
            customExpiryDate: editableExpiryDate,
          }
          : prev
      );
    } catch (error) {
      console.error("Failed to save expiry date:", error);
      alert("Failed to save expiry date.");
    } finally {
      setIsSavingExpiry(false);
    }
  };

  const handleResetExpiry = async () => {
    if (!selectedUser) return;

    try {
      setIsSavingExpiry(true);

      const payload = {
        customExpiryDate: null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "users", selectedUser.id), payload);

      if (selectedUser.slug) {
        await updateDoc(doc(db, "sites", selectedUser.slug), payload);
      }

      await loadUsers();

      const autoDate = getAutoExpiryDate(selectedUser);

      setSelectedUser((prev) =>
        prev
          ? {
            ...prev,
            customExpiryDate: null,
          }
          : prev
      );
      setEditableExpiryDate(toInputDateValue(autoDate));
    } catch (error) {
      console.error("Failed to reset expiry date:", error);
      alert("Failed to reset expiry date.");
    } finally {
      setIsSavingExpiry(false);
    }
  };

  const createBatchManager = () => {
    let batch = writeBatch(db);
    let operationCount = 0;

    const commitIfNeeded = async () => {
      if (operationCount >= MAX_BATCH_WRITES) {
        await batch.commit();
        batch = writeBatch(db);
        operationCount = 0;
      }
    };

    const set = async (ref, data, options) => {
      await commitIfNeeded();
      batch.set(ref, data, options);
      operationCount += 1;
    };

    const update = async (ref, data) => {
      await commitIfNeeded();
      batch.update(ref, data);
      operationCount += 1;
    };

    const del = async (ref) => {
      await commitIfNeeded();
      batch.delete(ref);
      operationCount += 1;
    };

    const commit = async () => {
      if (operationCount > 0) {
        await batch.commit();
        batch = writeBatch(db);
        operationCount = 0;
      }
    };

    return { set, update, del, commit };
  };

  const migrateGuestCollection = async ({
    userId,
    oldCollectionName,
    newCollectionName,
    batchManager,
  }) => {
    if (!userId || !oldCollectionName || !newCollectionName) return 0;
    if (oldCollectionName === newCollectionName) return 0;

    const oldCollectionRef = collection(db, "users", userId, oldCollectionName);
    const oldSnapshot = await getDocs(oldCollectionRef);

    if (oldSnapshot.empty) return 0;

    for (const guestDoc of oldSnapshot.docs) {
      const newDocRef = doc(
        db,
        "users",
        userId,
        newCollectionName,
        guestDoc.id
      );

      await batchManager.set(newDocRef, guestDoc.data(), { merge: true });
      await batchManager.del(guestDoc.ref);
    }

    return oldSnapshot.size;
  };

  const deleteTestFamilyOnUpgrade = async (userRecord) => {
    const userId = userRecord?.id;
    const slug = userRecord?.slug;

    if (!userId || !slug) return;

    const privateCollectionRef = collection(db, "users", userId, GROOM_COLLECTION);
    const privateQuery = query(
      privateCollectionRef,
      where("familyName", "==", TEST_FAMILY_NAME)
    );
    const privateSnapshot = await getDocs(privateQuery);

    for (const familyDoc of privateSnapshot.docs) {
      await deleteDoc(familyDoc.ref);

      const publicDocRef = doc(db, "sites", slug, GROOM_COLLECTION, familyDoc.id);
      await deleteDoc(publicDocRef);
    }
  };

  const handleBackfillShowImages = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to backfill section fields for all users?\n\nThis will add all section visibility fields as true to existing user and site records."
    );

    if (!confirmed) return;

    try {
      setIsBackfilling(true);

      const usersSnapshot = await getDocs(collection(db, "users"));
      const batchManager = createBatchManager();

      let updatedUsersCount = 0;
      let updatedSitesCount = 0;

      const defaultSectionFields = {
        isInviteSection: true,
        isCardComponent: true,
        isCountDown: true,
        isRsvp: true,
        isAgenda: true,
        isLoveNote: true,
        updatedAt: serverTimestamp(),
      };

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        const slug = userData.slug || "";

        const shouldUpdateUser =
          userData.isInviteSection !== true ||
          userData.isCardComponent !== true ||
          userData.isCountDown !== true ||
          userData.isRsvp !== true ||
          userData.isAgenda !== true ||
          userData.isLoveNote !== true;

        if (shouldUpdateUser) {
          await batchManager.update(
            doc(db, "users", userId),
            defaultSectionFields
          );
          updatedUsersCount += 1;
        }

        if (slug) {
          await batchManager.update(
            doc(db, "sites", slug),
            defaultSectionFields
          );
          updatedSitesCount += 1;
        }
      }

      await batchManager.commit();
      await loadUsers();

      alert(
        `Backfill completed successfully.\n\nUsers updated: ${updatedUsersCount}\nSites updated: ${updatedSitesCount}`
      );
    } catch (error) {
      console.error("Backfill failed:", error);
      alert("Backfill failed. Please check the console.");
    } finally {
      setIsBackfilling(false);
    }
  };

  const handleTogglePlan = async (user) => {
    const currentPlan = getNormalizedPlan(user);
    const nextPlan = currentPlan === "premium" ? "trial" : "premium";
    const nextIsTrial = nextPlan === "trial";
    const isDowngradingToTrial =
      currentPlan === "premium" && nextPlan === "trial";
    const isUpgradingToPremium =
      currentPlan === "trial" && nextPlan === "premium";

    try {
      setBusyId(user.id);

      const payload = {
        plan: nextPlan,
        isTrial: nextIsTrial,
        updatedAt: serverTimestamp(),
      };

      if (isDowngradingToTrial) {
        payload.addons = DEFAULT_ADDONS;
      }

      await updateDoc(doc(db, "users", user.id), payload);

      if (user.slug) {
        await updateDoc(doc(db, "sites", user.slug), payload);
      }

      if (isUpgradingToPremium) {
        await deleteTestFamilyOnUpgrade(user);
      }

      await loadUsers();

      if (selectedUser?.id === user.id) {
        const updatedSelectedUser = {
          ...selectedUser,
          plan: nextPlan,
          isTrial: nextIsTrial,
          addons: isDowngradingToTrial ? DEFAULT_ADDONS : selectedUser.addons,
        };

        setSelectedUser(updatedSelectedUser);
        setEditableExpiryDate(
          toInputDateValue(getEffectiveExpiryDate(updatedSelectedUser))
        );

        if (isDowngradingToTrial) {
          setAddonsForm(DEFAULT_ADDONS);
        }
      }
    } catch (error) {
      console.error("Failed to change plan:", error);
      alert("Failed to change plan.");
    } finally {
      setBusyId("");
    }
  };

  const handleDeleteUser = async (user) => {
    const authUid = getAuthUid(user);

    const confirmed = window.confirm(
      `Delete ${user.bride_name || "User"} & ${user.groom_name || "User"}?\n\nThis will delete the Firestore user record, related site record, and the Firebase Authentication account${authUid ? "" : " if a UID is available"
      }.\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setBusyId(user.id);

      if (authUid) {
        await deleteUserAccount({ uid: authUid });
      }

      await deleteDoc(doc(db, "users", user.id));

      if (user.slug) {
        await deleteDoc(doc(db, "sites", user.slug));
      }

      await loadUsers();

      if (selectedUser?.id === user.id) {
        closeDetailsModal();
      }

      if (selectedSiteSlug === user.slug) {
        setSelectedSiteSlug("");
      }

      if (!authUid) {
        alert(
          "Firestore records were deleted, but no Firebase Auth UID was found for this user. Save the Auth UID in the user document to delete the Authentication account too."
        );
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert(
        error?.message ||
        "Failed to delete user. Check that the Cloud Function is deployed and that the user document contains the Firebase Auth UID."
      );
    } finally {
      setBusyId("");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/adminlogin");
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to logout. Please try again.");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setPlanFilter("all");
    setStatusFilter("all");
    setAddonFilter("all");
    setRegisteredFrom("");
    setRegisteredTo("");
    setWeddingFrom("");
    setWeddingTo("");
    setExpiryFrom("");
    setExpiryTo("");
  };

  const totalUsers = users.length;
  const premiumUsers = users.filter(
    (user) => getNormalizedPlan(user) === "premium"
  ).length;
  const trialUsers = users.filter(
    (user) => getNormalizedPlan(user) === "trial"
  ).length;
  const expiredUsers = users.filter((user) => isExpiredUser(user)).length;

  return (
    <div className="adminDashboard">
      <div className="adminDashboard__topbar">
        <div>
          <p className="adminDashboard__eyebrow">Administration</p>
          <h1 className="adminDashboard__title">Admin Dashboard</h1>
          <p className="adminDashboard__subtitle">
            Manage registered couples, plans, expiry, site records, and add-ons.
          </p>
        </div>

        <div className="adminDashboard__topbarActions">
          <ButtonComponent onClick={loadUsers} disabled={isBackfilling}>
            Refresh
          </ButtonComponent>
          <ButtonComponent
            onClick={handleBackfillShowImages}
            disabled={isBackfilling}
          >
            {isBackfilling ? "Backfilling..." : "Backfill Everything"}
          </ButtonComponent>
          <ButtonComponent onClick={handleLogout} disabled={isBackfilling}>
            Logout
          </ButtonComponent>
        </div>
      </div>

      <div className="adminDashboard__stats">
        <div className="adminDashboard__statCard">
          <span className="adminDashboard__statLabel">Total Users</span>
          <strong className="adminDashboard__statValue">{totalUsers}</strong>
        </div>

        <div className="adminDashboard__statCard adminDashboard__statCard--trial">
          <span className="adminDashboard__statLabel">Trial Users</span>
          <strong className="adminDashboard__statValue">{trialUsers}</strong>
        </div>

        <div className="adminDashboard__statCard adminDashboard__statCard--premium">
          <span className="adminDashboard__statLabel">Premium Users</span>
          <strong className="adminDashboard__statValue">{premiumUsers}</strong>
        </div>

        <div className="adminDashboard__statCard adminDashboard__statCard--expired">
          <span className="adminDashboard__statLabel">Expired Users</span>
          <strong className="adminDashboard__statValue">{expiredUsers}</strong>
        </div>
      </div>

      <div className="adminDashboard__controls">
        <div className="adminDashboard__toolbar">
          <div className="adminDashboard__toolbarMain">
            <input
              type="text"
              className="adminDashboard__search"
              placeholder="Search couple, slug, email, or mobile"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <select
              className="adminDashboard__filter"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <option value="all">All Plans</option>
              <option value="trial">Trial</option>
              <option value="premium">Premium</option>
            </select>

            <select
              className="adminDashboard__filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>

            <select
              className="adminDashboard__filter"
              value={addonFilter}
              onChange={(e) => setAddonFilter(e.target.value)}
            >
              <option value="all">All Add-ons</option>
              <option value="withAddons">With Add-ons</option>
              <option value="withoutAddons">Without Add-ons</option>
            </select>
          </div>

          <div className="adminDashboard__dateFilters">
            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Registered From</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={registeredFrom}
                onChange={(e) => setRegisteredFrom(e.target.value)}
              />
            </div>

            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Registered To</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={registeredTo}
                onChange={(e) => setRegisteredTo(e.target.value)}
              />
            </div>

            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Wedding From</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={weddingFrom}
                onChange={(e) => setWeddingFrom(e.target.value)}
              />
            </div>

            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Wedding To</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={weddingTo}
                onChange={(e) => setWeddingTo(e.target.value)}
              />
            </div>

            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Expiry From</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={expiryFrom}
                onChange={(e) => setExpiryFrom(e.target.value)}
              />
            </div>

            <div className="adminDashboard__dateGroup">
              <label className="adminDashboard__dateLabel">Expiry To</label>
              <input
                type="date"
                className="adminDashboard__dateInput"
                value={expiryTo}
                onChange={(e) => setExpiryTo(e.target.value)}
              />
            </div>
          </div>

          <div className="adminDashboard__toolbarSecondary">
            <div className="adminDashboard__siteLinkBox">
              <span className="adminDashboard__siteLinkLabel">Site:</span>
              {selectedSiteSlug ? (
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="adminDashboard__siteLink"
                >
                  {`www.planmyguest.com/sites/${selectedSiteSlug}`}
                </a>
              ) : (
                <span className="adminDashboard__siteLinkPlaceholder">
                  Click a record to view site link
                </span>
              )}
            </div>

            <ButtonComponent
              className="adminDashboard__clearBtn"
              onClick={clearFilters}
            >
              Clear Filters
            </ButtonComponent>
          </div>
        </div>
      </div>

      {loading && <p className="adminDashboard__message">Loading dashboard...</p>}
      {pageError && <p className="adminDashboard__error">{pageError}</p>}

      {!loading && !pageError && filteredUsers.length === 0 && (
        <p className="adminDashboard__message">No users found.</p>
      )}

      {!loading && !pageError && filteredUsers.length > 0 && (
        <>
          <div className="adminDashboard__tableWrap">
            <table className="adminDashboard__table">
              <thead>
                <tr>
                  <th>Couple Names</th>
                  <th>Mobile</th>
                  <th>Package</th>
                  <th>Status</th>
                  <th>Wedding Date</th>
                  <th>Registered Date</th>
                  <th>Add-ons</th>
                  <th>Expiry</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedUsers.map((user) => {
                  const isPremium = isPremiumUser(user);
                  const isBusy = busyId === user.id;
                  const isExpired = isExpiredUser(user);

                  return (
                    <tr
                      key={user.id}
                      className="adminDashboard__clickableRow"
                      onClick={() => handleRowClick(user)}
                    >
                      <td>
                        <div className="adminDashboard__coupleName">
                          {user.bride_name || "Bride"} & {user.groom_name || "Groom"}
                        </div>
                      </td>

                      <td>{getMobileNumber(user)}</td>

                      <td>
                        <span
                          className={`adminDashboard__packageBadge ${isPremium
                            ? "adminDashboard__packageBadge--premium"
                            : "adminDashboard__packageBadge--trial"
                            }`}
                        >
                          {getPackageLabel(user)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`adminDashboard__statusBadge ${isExpired
                            ? "adminDashboard__statusBadge--expired"
                            : "adminDashboard__statusBadge--active"
                            }`}
                        >
                          {isExpired ? "Expired" : "Active"}
                        </span>
                      </td>

                      <td>{formatDate(user.wedding_date)}</td>
                      <td>{formatDate(user.createdAt)}</td>

                      <td>
                        <span className="adminDashboard__addonCount">
                          {getEnabledAddonCount(user)} enabled
                        </span>
                      </td>

                      <td>{getExpiryLabel(user)}</td>

                      <td>
                        <div
                          className="adminDashboard__tableActions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ButtonComponent
                            className="adminDashboard__addonsBtn"
                            onClick={() => openAddonsFromButton(user)}
                            disabled={!isPremium || isBusy || isBackfilling}
                            title={
                              isPremium
                                ? "Enable features for premium user"
                                : "Add-ons are only for premium"
                            }
                          >
                            Add-ons
                          </ButtonComponent>

                          <ButtonComponent
                            className={
                              isPremium
                                ? "adminDashboard__toggleBtn adminDashboard__toggleBtn--trial"
                                : "adminDashboard__toggleBtn adminDashboard__toggleBtn--premium"
                            }
                            onClick={() => handleTogglePlan(user)}
                            disabled={isBusy || isBackfilling}
                          >
                            {isBusy
                              ? "Updating..."
                              : isPremium
                                ? "Change to Trial"
                                : "Upgrade to Premium"}
                          </ButtonComponent>

                          <ButtonComponent
                            className="adminDashboard__dangerBtn"
                            onClick={() => handleDeleteUser(user)}
                            disabled={isBusy || isBackfilling}
                          >
                            {isBusy ? "Working..." : "Delete"}
                          </ButtonComponent>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="adminDashboard__mobileCards">
            {paginatedUsers.map((user) => {
              const isPremium = isPremiumUser(user);
              const isExpired = isExpiredUser(user);
              const isBusy = busyId === user.id;

              return (
                <div
                  key={user.id}
                  className="adminDashboard__mobileCard"
                  onClick={() => handleRowClick(user)}
                >
                  <div className="adminDashboard__mobileCardTop">
                    <div className="adminDashboard__mobileListName">
                      {user.bride_name || "Bride"} & {user.groom_name || "Groom"}
                    </div>

                    <div className="adminDashboard__mobileBadges">
                      <span
                        className={`adminDashboard__packageBadge ${isPremium
                          ? "adminDashboard__packageBadge--premium"
                          : "adminDashboard__packageBadge--trial"
                          }`}
                      >
                        {getPackageLabel(user)}
                      </span>

                      <span
                        className={`adminDashboard__statusBadge ${isExpired
                          ? "adminDashboard__statusBadge--expired"
                          : "adminDashboard__statusBadge--active"
                          }`}
                      >
                        {isExpired ? "Expired" : "Active"}
                      </span>
                    </div>
                  </div>

                  <div className="adminDashboard__mobileMeta">
                    <div><strong>Mobile:</strong> {getMobileNumber(user)}</div>
                    <div><strong>Wedding:</strong> {formatDate(user.wedding_date)}</div>
                    <div><strong>Registered:</strong> {formatDate(user.createdAt)}</div>
                    <div><strong>Expiry:</strong> {getExpiryLabel(user)}</div>
                    <div><strong>Add-ons:</strong> {getEnabledAddonCount(user)} enabled</div>
                  </div>

                  <div
                    className="adminDashboard__mobileActions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ButtonComponent
                      className="adminDashboard__addonsBtn"
                      onClick={() => openAddonsFromButton(user)}
                      disabled={!isPremium || isBusy || isBackfilling}
                    >
                      Add-ons
                    </ButtonComponent>

                    <ButtonComponent
                      className={
                        isPremium
                          ? "adminDashboard__toggleBtn adminDashboard__toggleBtn--trial"
                          : "adminDashboard__toggleBtn adminDashboard__toggleBtn--premium"
                      }
                      onClick={() => handleTogglePlan(user)}
                      disabled={isBusy || isBackfilling}
                    >
                      {isBusy
                        ? "Updating..."
                        : isPremium
                          ? "Change to Trial"
                          : "Upgrade"}
                    </ButtonComponent>

                    <ButtonComponent
                      className="adminDashboard__dangerBtn"
                      onClick={() => handleDeleteUser(user)}
                      disabled={isBusy || isBackfilling}
                    >
                      Delete
                    </ButtonComponent>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="adminDashboard__pagination">
            <button
              type="button"
              className="adminDashboard__pageBtn"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="adminDashboard__pageInfo">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="adminDashboard__pageBtn"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selectedUser && (
        <div className="adminDashboard__modalOverlay" onClick={closeDetailsModal}>
          <div className="adminDashboard__modal" onClick={(e) => e.stopPropagation()}>
            <div className="adminDashboard__modalHeader">
              <div className="adminDashboard__modalHeaderText">
                <p className="adminDashboard__modalEyebrow">User Details</p>
                <h2 className="adminDashboard__modalTitle">
                  {selectedUser.bride_name || "Bride"} & {selectedUser.groom_name || "Groom"}
                </h2>
                <p className="adminDashboard__modalSubTitle">
                  {selectedUser.slug || "—"}
                </p>
              </div>

              <button
                type="button"
                className="adminDashboard__modalClose"
                onClick={closeDetailsModal}
                disabled={isSavingAddons || isSavingExpiry}
              >
                ×
              </button>
            </div>

            <div className="adminDashboard__modalContent">
              <div className="adminDashboard__modalBody">
                <div className="adminDashboard__details">
                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Bride Name</span>
                    <span className="adminDashboard__detailValue">
                      {selectedUser.bride_name || "—"}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Groom Name</span>
                    <span className="adminDashboard__detailValue">
                      {selectedUser.groom_name || "—"}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Mobile</span>
                    <span className="adminDashboard__detailValue">
                      {getMobileNumber(selectedUser)}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Email</span>
                    <span className="adminDashboard__detailValue">
                      {selectedUser.email || "—"}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Auth UID</span>
                    <span className="adminDashboard__detailValue">
                      {getAuthUid(selectedUser) || "—"}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Package</span>
                    <span className="adminDashboard__detailValue">
                      <span
                        className={`adminDashboard__packageBadge ${isPremiumUser(selectedUser)
                          ? "adminDashboard__packageBadge--premium"
                          : "adminDashboard__packageBadge--trial"
                          }`}
                      >
                        {getPackageLabel(selectedUser)}
                      </span>
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Status</span>
                    <span className="adminDashboard__detailValue">
                      <span
                        className={`adminDashboard__statusBadge ${isExpiredUser(selectedUser)
                          ? "adminDashboard__statusBadge--expired"
                          : "adminDashboard__statusBadge--active"
                          }`}
                      >
                        {isExpiredUser(selectedUser) ? "Expired" : "Active"}
                      </span>
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Wedding Date</span>
                    <span className="adminDashboard__detailValue">
                      {formatDate(selectedUser.wedding_date)}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Registered Date</span>
                    <span className="adminDashboard__detailValue">
                      {formatDate(selectedUser.createdAt)}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Auto Expiry</span>
                    <span className="adminDashboard__detailValue">
                      {formatDate(getAutoExpiryDate(selectedUser))}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Current Expiry</span>
                    <span className="adminDashboard__detailValue">
                      {getExpiryLabel(selectedUser)}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem adminDashboard__detailItem--full">
                    <span className="adminDashboard__detailLabel">Editable Expiry Date</span>

                    <div className="adminDashboard__expiryEditor">
                      <input
                        type="date"
                        className="adminDashboard__dateInput adminDashboard__dateInput--full"
                        value={editableExpiryDate}
                        onChange={(e) => setEditableExpiryDate(e.target.value)}
                      />

                      <div className="adminDashboard__expiryActions">
                        <ButtonComponent
                          onClick={handleSaveExpiry}
                          disabled={isSavingExpiry}
                        >
                          {isSavingExpiry ? "Saving..." : "Save Expiry"}
                        </ButtonComponent>

                        <ButtonComponent
                          className="adminDashboard__resetBtn"
                          onClick={handleResetExpiry}
                          disabled={isSavingExpiry}
                        >
                          Reset Auto Expiry
                        </ButtonComponent>
                      </div>
                    </div>
                  </div>

                  <div className="adminDashboard__detailItem adminDashboard__detailItem--full">
                    <span className="adminDashboard__detailLabel">Site URL</span>
                    <span className="adminDashboard__detailValue">
                      {selectedUser.slug ? (
                        <a
                          href={`https://www.planmyguest.com/sites/${selectedUser.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="adminDashboard__siteLink"
                        >
                          {`www.planmyguest.com/sites/${selectedUser.slug}`}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">Add-ons</span>
                    <span className="adminDashboard__detailValue">
                      {getEnabledAddonCount(selectedUser)} enabled
                    </span>
                  </div>

                  <div className="adminDashboard__detailItem">
                    <span className="adminDashboard__detailLabel">User Type</span>
                    <span className="adminDashboard__detailValue">
                      {selectedUser.user_type || "—"}
                    </span>
                  </div>
                </div>

                <AdminGuestFamilySummary selectedUser={selectedUser} />

                {isPremiumUser(selectedUser) && (
                  <div className="adminDashboard__addonsSection">
                    <p className="adminDashboard__modalEyebrow">Add-ons</p>

                    <label className="adminDashboard__addonOption">
                      <input
                        type="checkbox"
                        checked={addonsForm.seatingArrangement}
                        onChange={() => handleAddonChange("seatingArrangement")}
                      />
                      <span>Seating Arrangements</span>
                    </label>

                    <label className="adminDashboard__addonOption">
                      <input
                        type="checkbox"
                        checked={addonsForm.budgetCalculation}
                        onChange={() => handleAddonChange("budgetCalculation")}
                      />
                      <span>Budget Calculation</span>
                    </label>

                    <label className="adminDashboard__addonOption">
                      <input
                        type="checkbox"
                        checked={addonsForm.qrCode}
                        onChange={() => handleAddonChange("qrCode")}
                      />
                      <span>QR Code</span>
                    </label>

                    <label className="adminDashboard__addonOption">
                      <input
                        type="checkbox"
                        checked={addonsForm.digitalInvite}
                        onChange={() => handleAddonChange("digitalInvite")}
                      />
                      <span>Guest RSVPs</span>
                    </label>

                    <label className="adminDashboard__addonOption">
                      <input
                        type="checkbox"
                        checked={addonsForm.musicChange}
                        onChange={() => handleAddonChange("musicChange")}
                      />
                      <span>Change Music</span>
                    </label>

                    <div className="adminDashboard__addonsActions">
                      <ButtonComponent
                        onClick={handleSaveAddons}
                        disabled={isSavingAddons}
                      >
                        {isSavingAddons ? "Saving..." : "Save Add-ons"}
                      </ButtonComponent>
                    </div>

                    <AdminBulkGuestImporter
                      selectedUser={selectedUser}
                      currentAdminUser={user}
                      onSuccess={loadUsers}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="adminDashboard__modalActions">
              <AdminInvoiceGenerator
                selectedUser={selectedUser}
                isPremium={isPremiumUser(selectedUser)}
              />

              <ButtonComponent
                className={
                  isPremiumUser(selectedUser)
                    ? "adminDashboard__toggleBtn adminDashboard__toggleBtn--trial"
                    : "adminDashboard__toggleBtn adminDashboard__toggleBtn--premium"
                }
                onClick={() => handleTogglePlan(selectedUser)}
                disabled={busyId === selectedUser.id || isBackfilling}
              >
                {busyId === selectedUser.id
                  ? "Updating..."
                  : isPremiumUser(selectedUser)
                    ? "Change to Trial"
                    : "Upgrade to Premium"}
              </ButtonComponent>

              <ButtonComponent
                className="adminDashboard__dangerBtn"
                onClick={() => handleDeleteUser(selectedUser)}
                disabled={busyId === selectedUser.id || isBackfilling}
              >
                {busyId === selectedUser.id ? "Working..." : "Delete"}
              </ButtonComponent>

              <ButtonComponent
                onClick={closeDetailsModal}
                disabled={isSavingAddons || isSavingExpiry}
              >
                Close
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;